import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, EmptyState } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trophy, Medal, Loader2, Calculator, BadgeCheck, Award, FileText, Download, Crown, Star } from "lucide-react";
import { toast } from "sonner";

const CAT = {
  OURO: { label: "Ouro", cls: "bg-amber-100 text-amber-700 border-amber-200", bar: "from-amber-400 to-yellow-500" },
  PRATA: { label: "Prata", cls: "bg-slate-200 text-slate-700 border-slate-300", bar: "from-slate-300 to-slate-400" },
  BRONZE: { label: "Bronze", cls: "bg-orange-100 text-orange-700 border-orange-200", bar: "from-orange-400 to-orange-600" },
};
const TERMS = ["1", "2", "3"];
const fmtAvg = (v) => (v != null ? Number(v).toFixed(1).replace(".", ",") : "—");
const fileUrl = (id) => `${process.env.REACT_APP_BACKEND_URL}/api/certificates/${id}/file`;

function useCertBatch() {
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const stop = () => { if (timer.current) { clearInterval(timer.current); timer.current = null; } };
  const start = useCallback(async (payload) => {
    try {
      stop();
      setJob(null); setOpen(true);
      const { data } = await api.post("/certificates/batch", payload);
      if (!data.total) { setJob({ status: "DONE", total: 0, generated: 0, failed: 0 }); toast.info("Nenhum estudante elegível."); return; }
      setJob({ status: "RUNNING", total: data.total, generated: 0, failed: 0 });
      timer.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/certificates/jobs/${data.job_id}`);
          setJob(j);
          if (j.status === "DONE") { stop(); toast.success(`${j.generated} certificado(s) gerados`); }
        } catch { stop(); }
      }, 1200);
    } catch (e) { stop(); toast.error(formatApiError(e.response?.data?.detail)); setOpen(false); }
  }, []);
  useEffect(() => () => stop(), []);
  return { job, open, setOpen, start };
}

function BatchProgress({ open, setOpen, job }) {
  const pct = job ? (job.total ? Math.round(((job.generated || 0) / job.total) * 100) : 100) : 0;
  const done = job && job.status === "DONE";
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && job && !done) return; setOpen(v); }}>
      <DialogContent className="sm:max-w-md" data-testid="batch-progress-dialog">
        <DialogHeader><DialogTitle>Gerando certificados em lote</DialogTitle></DialogHeader>
        <DialogDescription className="sr-only">Progresso da geração de certificados em lote</DialogDescription>
        {!job ? (
          <div className="flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Preparando...</div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-600">{done ? "Concluído" : "Processando..."}</span>
              <span className="font-bold text-blue-700" data-testid="batch-progress-count">{job.generated || 0}/{job.total || 0}</span>
            </div>
            <div className="h-3 w-full overflow-hidden rounded-full bg-slate-100">
              <div className="h-full rounded-full bg-orange-500 transition-all duration-300" style={{ width: `${pct}%` }} />
            </div>
            {job.failed ? <p className="text-xs text-red-500">{job.failed} falha(s)</p> : null}
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={job && !done} data-testid="batch-progress-close">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Premiacoes() {
  const { scopeParams, selectedSchoolId } = useAuth();
  const [years, setYears] = useState([]);
  const [yearId, setYearId] = useState("");

  useEffect(() => {
    api.get("/academic-years", { params: scopeParams }).then((r) => {
      setYears(r.data);
      if (r.data[0]) setYearId((y) => y || r.data[0].id);
    });
  }, [scopeParams, selectedSchoolId]);

  return (
    <div>
      <PageHeader title="Premiações" subtitle="Categorias Bronze/Prata/Ouro, Melhores do Ano e Central de Certificados">
        <Select value={yearId} onValueChange={setYearId}>
          <SelectTrigger className="w-40" data-testid="awards-year-select"><SelectValue placeholder="Ano letivo" /></SelectTrigger>
          <SelectContent>{years.map((y) => <SelectItem key={y.id} value={y.id}>{y.year}</SelectItem>)}</SelectContent>
        </Select>
      </PageHeader>

      <Tabs defaultValue="categorias">
        <div className="mb-5 w-full overflow-x-auto">
          <TabsList data-testid="awards-tabs" className="w-max">
            <TabsTrigger value="categorias" data-testid="tab-categorias"><Medal className="mr-1.5 h-4 w-4" /> Categorias</TabsTrigger>
            <TabsTrigger value="melhores" data-testid="tab-melhores"><Crown className="mr-1.5 h-4 w-4" /> Melhores do Ano</TabsTrigger>
            <TabsTrigger value="certificados" data-testid="tab-certificados"><FileText className="mr-1.5 h-4 w-4" /> Certificados</TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value="categorias"><CategoriasTab yearId={yearId} /></TabsContent>
        <TabsContent value="melhores"><MelhoresTab yearId={yearId} /></TabsContent>
        <TabsContent value="certificados"><CertificadosTab yearId={yearId} /></TabsContent>
      </Tabs>
    </div>
  );
}

function CategoriasTab({ yearId }) {
  const { scopeParams } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const load = useCallback(() => {
    if (!yearId) return;
    api.get("/awards", { params: { academic_year_id: yearId, ...scopeParams } })
      .then((r) => setData(r.data)).catch(() => setData({ results: [], counts: {}, total: 0 }));
  }, [yearId, scopeParams]);
  useEffect(() => { load(); }, [load]);

  const compute = async () => {
    setBusy(true);
    try { const { data: res } = await api.post("/awards/compute", { academic_year_id: yearId });
      toast.success(`Cálculo concluído · ${res.OURO || 0} Ouro, ${res.PRATA || 0} Prata, ${res.BRONZE || 0} Bronze`); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const homologate = async () => {
    setBusy(true);
    try { const { data: res } = await api.post("/awards/homologate", { academic_year_id: yearId });
      toast.success(`Homologado · ${res.categorized} premiações registradas`); setConfirm(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  const genCert = async (row) => {
    try { await api.post("/certificates/generate", { student_id: row.student_id, type: "CATEGORIA", academic_year_id: yearId, category: row.category });
      toast.success(`Certificado gerado para ${row.student_name}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const cb = useCertBatch();
  const batch = () => cb.start({ type: "CATEGORIA", academic_year_id: yearId });

  const c = data?.counts || {};
  return (
    <div>
      <div className="mb-5 flex flex-wrap gap-2">
        <Button variant="outline" onClick={compute} disabled={busy || !yearId} data-testid="awards-compute-button">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Calculator className="mr-1.5 h-4 w-4" /> Calcular</>}
        </Button>
        <Button onClick={() => setConfirm(true)} disabled={busy || !data?.total || data?.homologated} data-testid="awards-homologate-button" className="bg-blue-700 hover:bg-blue-800">
          <BadgeCheck className="mr-1.5 h-4 w-4" /> {data?.homologated ? "Homologado" : "Homologar"}
        </Button>
        <Button variant="outline" onClick={batch} disabled={busy || !data?.total} data-testid="awards-batch-cert-button">
          <FileText className="mr-1.5 h-4 w-4" /> Certificados em lote
        </Button>
      </div>
      <BatchProgress {...cb} />

      <div className="mb-5 grid grid-cols-3 gap-3">
        {["OURO", "PRATA", "BRONZE"].map((k) => (
          <Card key={k} className={`border p-5 ${CAT[k].cls}`} data-testid={`category-count-${k}`}>
            <Medal className="h-7 w-7" />
            <div className="mt-2 font-display text-4xl font-black">{c[k] || 0}</div>
            <div className="text-sm font-semibold">{CAT[k].label}</div>
          </Card>
        ))}
      </div>

      {!data || data.results.length === 0 ? (
        <EmptyState icon={Trophy} title="Sem premiações" description="Selecione o ano e clique em Calcular. Bronze = destaque em 1 trimestre, Prata = 2, Ouro = 3 (Excelência homologada)." />
      ) : (
        <Card className="overflow-x-auto border-slate-200" data-testid="awards-table">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Estudante</TableHead><TableHead>Turma</TableHead>
              <TableHead className="text-center">Vezes destaque</TableHead>
              {TERMS.map((t) => <TableHead key={t} className="text-center">{t}º Tri</TableHead>)}
              <TableHead className="text-center">Categoria</TableHead>
              <TableHead className="text-right">Certificado</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {data.results.map((r) => (
                <TableRow key={r.id} data-testid={`award-row-${r.student_id}`}>
                  <TableCell className="font-medium text-slate-800">{r.student_name}</TableCell>
                  <TableCell className="text-slate-500">{r.class_name || "—"}</TableCell>
                  <TableCell className="text-center font-semibold" data-testid={`award-terms-count-${r.student_id}`}>{r.terms_count || 0}</TableCell>
                  {TERMS.map((t) => <TableCell key={t} className="text-center" data-testid={`award-term-${t}-${r.student_id}`}>{fmtAvg(r.term_avgs?.[t])}</TableCell>)}
                  <TableCell className="text-center">
                    {r.category ? <Badge className={`border ${CAT[r.category].cls}`}>{CAT[r.category].label}</Badge>
                      : <span className="text-xs text-slate-400">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {r.category && (
                      <Button size="sm" variant="ghost" onClick={() => genCert(r)} data-testid={`award-gen-cert-${r.student_id}`}>
                        <Award className="mr-1 h-4 w-4" /> Gerar
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      <Dialog open={confirm} onOpenChange={setConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Homologar Premiações</DialogTitle></DialogHeader>
          <p className="text-sm text-slate-600">A homologação cria um snapshot histórico e gera as conquistas de categoria (selos) no perfil dos estudantes. Registros homologados não são sobrescritos em novos cálculos.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirm(false)}>Cancelar</Button>
            <Button onClick={homologate} disabled={busy} data-testid="awards-homologate-confirm" className="bg-blue-700 hover:bg-blue-800">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirmar homologação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MelhoresTab({ yearId }) {
  const { scopeParams } = useAuth();
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!yearId) return;
    api.get("/awards/best-of-year", { params: { academic_year_id: yearId, limit: 3, ...scopeParams } })
      .then((r) => setData(r.data)).catch(() => setData({ overall: [], by_class: [] }));
  }, [yearId, scopeParams]);
  useEffect(() => { load(); }, [load]);

  const cb = useCertBatch();
  const batch = () => cb.start({ type: "MELHORES_ANO", academic_year_id: yearId, limit: 3 });

  const hasData = data && (data.overall?.length || data.by_class?.length);
  return (
    <div>
      <BatchProgress {...cb} />
      <div className="mb-5">
        <Button variant="outline" onClick={batch} disabled={!hasData} data-testid="best-batch-cert-button">
          <FileText className="mr-1.5 h-4 w-4" /> Gerar certificados dos destaques
        </Button>
      </div>
      {!hasData ? (
        <EmptyState icon={Crown} title="Sem destaques ainda" description="Calcule as premiações na aba Categorias. O ranking considera quantas vezes o estudante foi destaque nos trimestres." />
      ) : (
        <div className="space-y-5">
          <Card className="border-slate-200 p-5" data-testid="best-overall">
            <div className="mb-3 flex items-center gap-2 font-display text-lg font-bold text-slate-900"><Crown className="h-5 w-5 text-amber-500" /> Destaque Geral da Escola</div>
            <Podium list={data.overall} />
          </Card>
          <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {data.by_class.map((g) => (
              <Card key={g.class_id} className="border-slate-200 p-5" data-testid={`best-class-${g.class_id}`}>
                <div className="mb-3 font-display font-bold text-slate-900">Turma {g.class_name}</div>
                <Podium list={g.students} />
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Podium({ list }) {
  const medals = ["text-amber-500", "text-slate-400", "text-orange-500"];
  if (!list || !list.length) return <p className="text-sm text-slate-400">Sem dados.</p>;
  return (
    <ol className="space-y-2">
      {list.map((r, i) => (
        <li key={r.id || r.student_id} className="flex items-center gap-3 rounded-lg bg-slate-50 px-3 py-2">
          <Star className={`h-5 w-5 ${medals[i] || "text-slate-300"}`} />
          <span className="flex-1 truncate text-sm font-medium text-slate-800">{i + 1}º {r.student_name}</span>
          {r.category && <Badge className={`border ${CAT[r.category].cls}`}>{CAT[r.category].label}</Badge>}
          <span className="text-sm font-bold text-blue-700">{r.terms_count || 0}× destaque</span>
          <span className="hidden text-xs text-slate-400 sm:inline">{TERMS.map((t) => `${t}º ${fmtAvg(r.term_avgs?.[t])}`).join(" · ")}</span>
        </li>
      ))}
    </ol>
  );
}

function CertificadosTab({ yearId }) {
  const { scopeParams } = useAuth();
  const [rows, setRows] = useState([]);
  const [type, setType] = useState("all");

  const load = useCallback(() => {
    if (!yearId) return;
    const params = { academic_year_id: yearId, ...scopeParams };
    if (type !== "all") params.type = type;
    api.get("/certificates", { params }).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [yearId, type, scopeParams]);
  useEffect(() => { load(); }, [load]);

  const TYPE_LABEL = { EXCELENCIA: "Excelência (Trimestre)", CATEGORIA: "Categoria Anual", MELHORES_ANO: "Melhores do Ano" };
  return (
    <div>
      <div className="mb-5 flex flex-wrap items-center gap-3">
        <Select value={type} onValueChange={setType}>
          <SelectTrigger className="w-56" data-testid="cert-type-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os tipos</SelectItem>
            <SelectItem value="CATEGORIA">Categoria Anual</SelectItem>
            <SelectItem value="MELHORES_ANO">Melhores do Ano</SelectItem>
            <SelectItem value="EXCELENCIA">Excelência (Trimestre)</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-slate-400">{rows.length} certificado(s)</span>
      </div>
      {rows.length === 0 ? (
        <EmptyState icon={FileText} title="Nenhum certificado" description="Gere certificados nas abas Categorias ou Melhores do Ano." />
      ) : (
        <Card className="overflow-x-auto border-slate-200" data-testid="cert-table">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Estudante</TableHead><TableHead>Tipo</TableHead>
              <TableHead>Detalhe</TableHead><TableHead className="text-right">Ação</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-testid={`cert-row-${r.id}`}>
                  <TableCell className="font-medium text-slate-800">{r.student_name}</TableCell>
                  <TableCell><Badge variant="outline">{TYPE_LABEL[r.type] || r.type}</Badge></TableCell>
                  <TableCell className="text-slate-500">{r.category ? CAT[r.category]?.label : r.scope || (r.term_order ? `${r.term_order}º Tri` : "—")}</TableCell>
                  <TableCell className="text-right">
                    <a href={fileUrl(r.id)} target="_blank" rel="noreferrer" data-testid={`cert-open-${r.id}`}>
                      <Button size="sm" variant="ghost"><Download className="mr-1 h-4 w-4" /> Abrir PDF</Button>
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Card>
      )}
    </div>
  );
}
